// Employment constants
export const EMPLOYMENT_TYPES = ["permanent", "casual", "part-time"];
export const SALARY_TYPES = ["monthly", "hourly", "casual"];
export const LEAVE_TYPES = ["annual", "sick", "family", "unpaid"];

// Default employee form data
export const defaultEmployee = {
  employee_number: "",
  first_name: "",
  last_name: "",
  id_number: "",
  date_of_birth: "",
  contact_number: "",
  email: "",
  address: "",
  position: "",
  department: "",
  employment_type: "permanent",
  salary_type: "monthly",
  basic_salary: 0,
  bank_name: "",
  bank_account_number: "",
  bank_branch_code: "",
  tax_number: "",
  start_date: new Date().toISOString().split("T")[0],
  annual_leave_days: 15,
  sick_leave_days: 30,
  family_leave_days: 3,
  pin: "",  // 4-digit PIN for clock in/out
  is_salesperson: false,
  marketing_number: "",
};

// Default payslip form data
export const defaultPayslipData = {
  pay_period_start: "",
  pay_period_end: "",
  hours_worked: 0,
  overtime_hours: 0,
  bonus: 0,
  commission: 0,
  other_earnings: 0,
  other_earnings_description: "",
  loan_deduction: 0,
  other_deductions: 0,
  other_deductions_description: "",
};

// Default leave request data
export const defaultLeaveData = {
  employee_id: "",
  leave_type: "annual",
  start_date: "",
  end_date: "",
  days: 0,
  reason: "",
};

// South African PAYE Tax calculation (2024/2025)
export const calculatePAYE = (annualTaxableIncome, age = 30) => {
  // Tax thresholds
  const threshold = age >= 75 ? 165689 : age >= 65 ? 148217 : 95750;
  if (annualTaxableIncome <= threshold) return 0;
  
  // Tax brackets
  const brackets = [
    { limit: 237100, rate: 0.18, base: 0 },
    { limit: 370500, rate: 0.26, base: 42678 },
    { limit: 512800, rate: 0.31, base: 77362 },
    { limit: 673000, rate: 0.36, base: 121475 },
    { limit: 857900, rate: 0.39, base: 179147 },
    { limit: 1817000, rate: 0.41, base: 251258 },
    { limit: Infinity, rate: 0.45, base: 644489 }
  ];
  
  let tax = 0;
  let prevLimit = 0;
  
  for (const bracket of brackets) {
    if (annualTaxableIncome <= bracket.limit) {
      tax = bracket.base + (annualTaxableIncome - prevLimit) * bracket.rate;
      break;
    }
    prevLimit = bracket.limit;
  }
  
  // Apply rebates
  const primaryRebate = 17235;
  const secondaryRebate = age >= 65 ? 9444 : 0;
  const tertiaryRebate = age >= 75 ? 3145 : 0;
  
  return Math.max(0, tax - primaryRebate - secondaryRebate - tertiaryRebate);
};

// UIF calculation (1% capped at R17,712)
export const calculateUIF = (monthlyGross) => {
  const uifBase = Math.min(monthlyGross, 17712);
  return Math.round(uifBase * 0.01 * 100) / 100;
};

// Calculate payslip details
export const calculatePayslip = (employee, payslipData) => {
  if (!employee) return null;
  
  // Calculate basic pay
  let basicPay = 0;
  let overtimePay = 0;
  
  if (employee.salary_type === "monthly") {
    basicPay = employee.basic_salary || 0;
    // Overtime at 1.5x hourly rate (assuming 160 hours/month)
    const hourlyRate = basicPay / 160;
    overtimePay = hourlyRate * 1.5 * payslipData.overtime_hours;
  } else {
    // Hourly employee
    basicPay = (employee.basic_salary || 0) * payslipData.hours_worked;
    overtimePay = (employee.basic_salary || 0) * 1.5 * payslipData.overtime_hours;
  }
  
  // Calculate gross earnings
  const grossEarnings = basicPay + overtimePay + payslipData.bonus + payslipData.commission + payslipData.other_earnings;
  
  // Calculate deductions
  const uifDeduction = calculateUIF(grossEarnings);
  const annualTaxable = grossEarnings * 12;
  const annualPAYE = calculatePAYE(annualTaxable);
  const monthlyPAYE = Math.round(annualPAYE / 12 * 100) / 100;
  
  const totalDeductions = uifDeduction + monthlyPAYE + payslipData.loan_deduction + payslipData.other_deductions;
  const netPay = grossEarnings - totalDeductions;
  
  return {
    employee,
    basicPay: Math.round(basicPay * 100) / 100,
    overtimePay: Math.round(overtimePay * 100) / 100,
    grossEarnings: Math.round(grossEarnings * 100) / 100,
    uifDeduction,
    payeDeduction: monthlyPAYE,
    totalDeductions: Math.round(totalDeductions * 100) / 100,
    netPay: Math.round(netPay * 100) / 100
  };
};

// Print payslip helper
export const printPayslip = (payslip) => {
  const printWindow = window.open("", "_blank");
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>Payslip - ${payslip.employee_name}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; }
        .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 20px; }
        .company-name { font-size: 24px; font-weight: bold; }
        .payslip-title { font-size: 18px; color: #666; }
        .section { margin-bottom: 20px; }
        .section-title { font-weight: bold; background: #f0f0f0; padding: 8px; margin-bottom: 10px; }
        .row { display: flex; justify-content: space-between; padding: 5px 0; border-bottom: 1px solid #eee; }
        .row label { color: #666; }
        .row span { font-weight: 500; }
        .total-row { font-weight: bold; font-size: 16px; background: #f8f8f8; padding: 10px; }
        .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
        @media print { body { padding: 0; } }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="company-name">BOSVELD BOU & HARDEWARE</div>
        <div class="payslip-title">PAYSLIP</div>
        <div>Period: ${payslip.pay_period_start} to ${payslip.pay_period_end}</div>
      </div>
      
      <div class="grid">
        <div class="section">
          <div class="section-title">Employee Details</div>
          <div class="row"><label>Name:</label><span>${payslip.employee_name}</span></div>
          <div class="row"><label>Employee No:</label><span>${payslip.employee_number}</span></div>
          <div class="row"><label>Position:</label><span>${payslip.position}</span></div>
          <div class="row"><label>ID Number:</label><span>${payslip.id_number || "-"}</span></div>
          <div class="row"><label>Tax Number:</label><span>${payslip.tax_number || "-"}</span></div>
        </div>
        
        <div class="section">
          <div class="section-title">Bank Details</div>
          <div class="row"><label>Bank:</label><span>${payslip.bank_details?.bank_name || "-"}</span></div>
          <div class="row"><label>Account:</label><span>${payslip.bank_details?.account_number || "-"}</span></div>
          <div class="row"><label>Branch:</label><span>${payslip.bank_details?.branch_code || "-"}</span></div>
        </div>
      </div>
      
      <div class="grid">
        <div class="section">
          <div class="section-title">Earnings</div>
          <div class="row"><label>Basic Salary:</label><span>R ${payslip.basic_pay.toFixed(2)}</span></div>
          ${payslip.overtime_pay > 0 ? `<div class="row"><label>Overtime (${payslip.overtime_hours} hrs):</label><span>R ${payslip.overtime_pay.toFixed(2)}</span></div>` : ''}
          ${payslip.bonus > 0 ? `<div class="row"><label>Bonus:</label><span>R ${payslip.bonus.toFixed(2)}</span></div>` : ''}
          ${payslip.commission > 0 ? `<div class="row"><label>Commission:</label><span>R ${payslip.commission.toFixed(2)}</span></div>` : ''}
          ${payslip.other_earnings > 0 ? `<div class="row"><label>${payslip.other_earnings_description || 'Other'}:</label><span>R ${payslip.other_earnings.toFixed(2)}</span></div>` : ''}
          <div class="row total-row"><label>Gross Earnings:</label><span>R ${payslip.gross_earnings.toFixed(2)}</span></div>
        </div>
        
        <div class="section">
          <div class="section-title">Deductions</div>
          <div class="row"><label>PAYE Tax:</label><span>R ${payslip.paye_deduction.toFixed(2)}</span></div>
          <div class="row"><label>UIF (1%):</label><span>R ${payslip.uif_deduction.toFixed(2)}</span></div>
          ${payslip.loan_deduction > 0 ? `<div class="row"><label>Loan Repayment:</label><span>R ${payslip.loan_deduction.toFixed(2)}</span></div>` : ''}
          ${payslip.other_deductions > 0 ? `<div class="row"><label>${payslip.other_deductions_description || 'Other'}:</label><span>R ${payslip.other_deductions.toFixed(2)}</span></div>` : ''}
          <div class="row total-row"><label>Total Deductions:</label><span>R ${payslip.total_deductions.toFixed(2)}</span></div>
        </div>
      </div>
      
      <div class="section" style="text-align: center; margin-top: 30px;">
        <div class="total-row" style="font-size: 20px; background: #e8f5e9;">
          <label>NET PAY: </label><span>R ${payslip.net_pay.toFixed(2)}</span>
        </div>
      </div>
      
      <div style="margin-top: 40px; font-size: 12px; color: #666; text-align: center;">
        Generated on ${new Date(payslip.created_at).toLocaleDateString()}
      </div>
      
      <script>window.onload = function() { window.print(); }</script>
    </body>
    </html>
  `);
  printWindow.document.close();
};
